import { useState, useMemo } from "react";
import { Edit, Trash2, Plus, ClipboardList, FileSpreadsheet } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { NumericInput } from "../ui/numeric-input";
import { Label } from "../ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { formatCurrency } from "./utils";
import { formatDate } from "../../lib/formatters";

const ChangeOrdersTab = ({
  job,
  canManageJobs,
  totalChangeOrders,
  effectiveContract,
  onAddChangeOrder,
  onEditChangeOrder,
  onDeleteChangeOrder,
}) => {
  const [coDialogOpen, setCoDialogOpen] = useState(false);
  const [coForm, setCoForm] = useState({
    co_number: "",
    labor_amount: "",
    material_amount: "",
    equipment_amount: "",
    date_approved: "",
  });
  const [editCoDialogOpen, setEditCoDialogOpen] = useState(false);
  const [editingCo, setEditingCo] = useState(null);

  // Compute next available CO number
  const nextCoNumber = useMemo(() => {
    const existingNumbers = (job.change_orders || []).map(co => {
      const num = parseInt(co.co_number, 10);
      return isNaN(num) ? 0 : num;
    });
    const maxNum = existingNumbers.length > 0 ? Math.max(...existingNumbers) : 0;
    return (maxNum + 1).toString();
  }, [job.change_orders]);

  // Check if a CO# is already taken
  const isDuplicateNumber = (num, excludeId) => {
    if (!num) return false;
    return (job.change_orders || []).some(co => co.co_number === num && co.id !== excludeId);
  };

  const resetForm = () => {
    setCoForm({
      co_number: "",
      labor_amount: "",
      material_amount: "",
      equipment_amount: "",
      date_approved: "",
    });
  };

  const calculateTotal = () => {
    const labor = parseFloat(coForm.labor_amount) || 0;
    const material = parseFloat(coForm.material_amount) || 0;
    const equipment = parseFloat(coForm.equipment_amount) || 0;
    return labor + material + equipment;
  };

  const handleAddChangeOrder = async (e) => {
    e.preventDefault();
    const success = await onAddChangeOrder({
      co_number: coForm.co_number,
      labor_amount: parseFloat(coForm.labor_amount) || 0,
      material_amount: parseFloat(coForm.material_amount) || 0,
      equipment_amount: parseFloat(coForm.equipment_amount) || 0,
      date_approved: coForm.date_approved || null,
    });
    if (success) {
      setCoDialogOpen(false);
      resetForm();
    }
  };

  const openEditChangeOrder = (co) => {
    setEditingCo(co);
    setCoForm({
      co_number: co.co_number,
      labor_amount: (co.labor_amount || 0).toString(),
      material_amount: (co.material_amount || 0).toString(),
      equipment_amount: (co.equipment_amount || 0).toString(),
      date_approved: co.date_approved || "",
    });
    setEditCoDialogOpen(true);
  };

  const handleEditChangeOrder = async () => {
    const success = await onEditChangeOrder(editingCo.id, {
      co_number: coForm.co_number,
      labor_amount: parseFloat(coForm.labor_amount) || 0,
      material_amount: parseFloat(coForm.material_amount) || 0,
      equipment_amount: parseFloat(coForm.equipment_amount) || 0,
      date_approved: coForm.date_approved || "",
    });
    if (success) {
      setEditCoDialogOpen(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg">Change Orders</CardTitle>
          {canManageJobs && (
            <Dialog open={coDialogOpen} onOpenChange={(open) => { 
              setCoDialogOpen(open); 
              if (open) setCoForm(f => ({ ...f, co_number: nextCoNumber }));
              if (!open) resetForm(); 
            }}>
              <DialogTrigger asChild>
                <Button size="sm" data-testid="add-co-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Change Order
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add Change Order</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleAddChangeOrder} className="space-y-4">
                  <div className="form-group">
                    <Label>CO # *</Label>
                    <Input 
                      value={coForm.co_number} 
                      onChange={(e) => setCoForm({ ...coForm, co_number: e.target.value })} 
                      required 
                      placeholder="e.g., 1" 
                      data-testid="co-number-input" 
                    />
                    {isDuplicateNumber(coForm.co_number) && (
                      <p className="text-xs text-red-600 mt-1">CO #{coForm.co_number} already exists</p>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-1 gap-4">
                    <div className="form-group">
                      <Label>Labor Amount *</Label>
                      <NumericInput 
                        decimals={2}
                        allowNegative={true}
                        value={coForm.labor_amount} 
                        onChange={(e) => setCoForm({ ...coForm, labor_amount: e.target.value })} 
                        placeholder="0.00"
                        data-testid="co-labor-input" 
                      />
                    </div>
                    <div className="form-group">
                      <Label>Material Amount *</Label>
                      <NumericInput 
                        decimals={2}
                        allowNegative={true}
                        value={coForm.material_amount} 
                        onChange={(e) => setCoForm({ ...coForm, material_amount: e.target.value })} 
                        placeholder="0.00"
                        data-testid="co-material-input" 
                      />
                    </div>
                    <div className="form-group">
                      <Label>Equipment Amount *</Label>
                      <NumericInput 
                        decimals={2}
                        allowNegative={true}
                        value={coForm.equipment_amount} 
                        onChange={(e) => setCoForm({ ...coForm, equipment_amount: e.target.value })} 
                        placeholder="0.00"
                        data-testid="co-equipment-input" 
                      />
                    </div>
                  </div>

                  <div className="p-3 bg-muted/30 rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Total CO Amount</span>
                      <span className={`text-lg font-bold ${calculateTotal() >= 0 ? "text-green-600" : "text-red-600"}`}>
                        {calculateTotal() >= 0 ? "+" : ""}{formatCurrency(calculateTotal())}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Use negative values for deductions</p>
                  </div>

                  <div className="form-group">
                    <Label>Date Approved</Label>
                    <Input 
                      type="date" 
                      value={coForm.date_approved} 
                      onChange={(e) => setCoForm({ ...coForm, date_approved: e.target.value })} 
                      data-testid="co-date-input" 
                    />
                  </div>
                  
                  <Button type="submit" className="w-full" data-testid="submit-co-btn" disabled={isDuplicateNumber(coForm.co_number)}>
                    Add Change Order
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </CardHeader>
        <CardContent>
          {job.change_orders?.length > 0 ? (
            <>
              <Table>
                <TableHeader>
                  <TableRow className="table-header">
                    <TableHead>CO #</TableHead>
                    <TableHead>Date Approved</TableHead>
                    <TableHead className="text-right">Labor</TableHead>
                    <TableHead className="text-right">Material</TableHead>
                    <TableHead className="text-right">Equipment</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    {canManageJobs && <TableHead className="w-12"></TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {job.change_orders.map((co) => {
                    const coTotal = parseFloat(co.amount) || 0;
                    return (
                    <TableRow key={co.id} className="table-row">
                      <TableCell className="font-medium">
                        {co.co_number}
                        {co.source_bid_sheet_id && (
                          <span className="ml-2 inline-flex items-center text-xs text-blue-600" title="Created from Bid Sheet">
                            <FileSpreadsheet className="w-3 h-3 mr-0.5" />Bid Sheet
                          </span>
                        )}
                      </TableCell>
                      <TableCell>{formatDate(co.date_approved) || "-"}</TableCell>
                      <TableCell className={`text-right font-mono ${(co.labor_amount || 0) >= 0 ? "text-foreground" : "text-red-600"}`}>
                        {formatCurrency(co.labor_amount || 0)}
                      </TableCell>
                      <TableCell className={`text-right font-mono ${(co.material_amount || 0) >= 0 ? "text-foreground" : "text-red-600"}`}>
                        {formatCurrency(co.material_amount || 0)}
                      </TableCell>
                      <TableCell className={`text-right font-mono ${(co.equipment_amount || 0) >= 0 ? "text-foreground" : "text-red-600"}`}>
                        {formatCurrency(co.equipment_amount || 0)}
                      </TableCell>
                      <TableCell className={`text-right font-mono font-bold ${coTotal >= 0 ? "text-green-600" : "text-red-600"}`}>
                        {coTotal >= 0 ? "+" : ""}{formatCurrency(coTotal)}
                      </TableCell>
                      {canManageJobs && (
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEditChangeOrder(co)}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive">
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete Change Order?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    This will permanently remove this change order and its associated estimate line items.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => onDeleteChangeOrder(co.id)} className="bg-destructive text-destructive-foreground">Delete</AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                    );
                  })}
                  <TableRow className="bg-muted/30 font-bold">
                    <TableCell colSpan={5}>Total Change Orders</TableCell>
                    <TableCell className={`text-right ${totalChangeOrders >= 0 ? "text-green-600" : "text-red-600"}`}>
                      {totalChangeOrders >= 0 ? "+" : ""}{formatCurrency(totalChangeOrders)}
                    </TableCell>
                    {canManageJobs && <TableCell></TableCell>}
                  </TableRow>
                </TableBody>
              </Table>
              <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex justify-between items-center">
                  <span className="text-blue-800">
                    <span className="font-medium">Base Contract:</span> {formatCurrency(job.contract_amount)}
                  </span>
                  <span className="text-xl font-bold text-blue-700">
                    Effective Contract: {formatCurrency(effectiveContract)}
                  </span>
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-3">
                Note: CO amounts are automatically added to Labor, Material, and Equipment estimates as line items.
              </p>
            </>
          ) : (
            <div className="text-center py-8">
              <ClipboardList className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No change orders recorded</p>
              <p className="text-sm text-muted-foreground mt-1">Change orders automatically adjust the contract amount and add to estimates</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Change Order Dialog */}
      <Dialog open={editCoDialogOpen} onOpenChange={(open) => { setEditCoDialogOpen(open); if (!open) resetForm(); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit Change Order</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="form-group">
              <Label>CO # *</Label>
              <Input 
                value={coForm.co_number} 
                onChange={(e) => setCoForm({ ...coForm, co_number: e.target.value })} 
                placeholder="e.g., 1" 
              />
              {isDuplicateNumber(coForm.co_number, editingCo?.id) && (
                <p className="text-xs text-red-600 mt-1">CO #{coForm.co_number} already exists</p>
              )}
            </div>
            
            <div className="grid grid-cols-1 gap-4">
              <div className="form-group">
                <Label>Labor Amount *</Label>
                <NumericInput 
                  decimals={2}
                  allowNegative={true}
                  value={coForm.labor_amount} 
                  onChange={(e) => setCoForm({ ...coForm, labor_amount: e.target.value })} 
                  placeholder="0.00"
                />
              </div>
              <div className="form-group">
                <Label>Material Amount *</Label>
                <NumericInput 
                  decimals={2}
                  allowNegative={true}
                  value={coForm.material_amount} 
                  onChange={(e) => setCoForm({ ...coForm, material_amount: e.target.value })} 
                  placeholder="0.00"
                />
              </div>
              <div className="form-group">
                <Label>Equipment Amount *</Label>
                <NumericInput 
                  decimals={2}
                  allowNegative={true}
                  value={coForm.equipment_amount} 
                  onChange={(e) => setCoForm({ ...coForm, equipment_amount: e.target.value })} 
                  placeholder="0.00"
                />
              </div>
            </div>

            <div className="p-3 bg-muted/30 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Total CO Amount</span>
                <span className={`text-lg font-bold ${calculateTotal() >= 0 ? "text-green-600" : "text-red-600"}`}>
                  {calculateTotal() >= 0 ? "+" : ""}{formatCurrency(calculateTotal())}
                </span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">Use negative values for deductions</p>
            </div>

            <div className="form-group">
              <Label>Date Approved</Label>
              <Input 
                type="date" 
                value={coForm.date_approved} 
                onChange={(e) => setCoForm({ ...coForm, date_approved: e.target.value })} 
              />
            </div>
            
            <Button onClick={handleEditChangeOrder} className="w-full" disabled={isDuplicateNumber(coForm.co_number, editingCo?.id)}>
              Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ChangeOrdersTab;
